import { api } from './client';

/**
 * Признаки самостоятельности письменных работ — очередь куратора и пороги.
 *
 * Ручек «для ученика» здесь нет и быть не может: факты о том, как писалась
 * работа, видит только куратор. Обвинение, выставленное автоматом и увиденное
 * учеником, разрушает доверие быстрее, чем списывание — учёбу.
 *
 * Сервер: services/progress-service/internal/handler/authorship_handler.go
 */

/** Один факт: код для статистики, текст для чтения. */
export interface AuthorshipFact {
  code: string;
  text: string;
}

export interface AuthorshipReport {
  v: number;
  /** Сигналы. Пусто — работа в очередь не попадает. */
  flags: AuthorshipFact[];
  /** Факты без обвинения: сколько писал, сколько правил. */
  context: AuthorshipFact[];
}

export type WorkKind = 'homework' | 'essay';
export type AuthorshipDecision = 'ok' | 'talked';

export interface FlaggedWork {
  kind: WorkKind;
  id: string;
  student_id: string;
  student_name: string;
  title: string;
  excerpt: string;
  created_at: string;
  report: AuthorshipReport;
  decision?: AuthorshipDecision;
  band?: number;
}

export interface AuthorshipThresholds {
  min_words: number;
  paste_share: number;
  largest_paste_words: number;
  max_wpm: number;
  no_edits_min_words: number;
  band_gap_profile: number;
  band_gap_history: number;
  min_past_works: number;
}

/** Сколько раз сигнал оказался пустым, а сколько — поводом для разговора. */
export interface AuthorshipCalibration {
  code: string;
  ok: number;
  talked: number;
}

export const authorshipApi = {
  async listWorks(limit = 50) {
    const { data } = await api.get('/curator/authorship/works', { params: { limit } });
    return data as { items: FlaggedWork[]; total: number };
  },

  async review(kind: WorkKind, id: string, decision: AuthorshipDecision, note?: string) {
    await api.post(`/curator/authorship/works/${kind}/${id}/review`, { decision, note });
  },

  async getSettings() {
    const { data } = await api.get('/admin/authorship/settings');
    return data as AuthorshipThresholds;
  },

  async saveSettings(payload: AuthorshipThresholds) {
    const { data } = await api.put('/admin/authorship/settings', payload);
    return data as AuthorshipThresholds;
  },

  async getCalibration() {
    const { data } = await api.get('/admin/authorship/calibration');
    return (data as { items: AuthorshipCalibration[] }).items ?? [];
  },
};
